import type { RefObject } from 'react';

import { useCallback, useEffect, useRef } from 'react';

import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { RecentlyViewedProduct } from '~/utilities/helpers';
import { getProductDataLayerEvent, parseProductForImpressions } from '~/utilities/vendors';
import type { SectionNameType } from '~/utilities/vendors/dataLayer.type';

export const useProductImpressionTracking = ({
  productToSolr,
  sectionName,
  position,
}: {
  position: number;
  sectionName: SectionNameType;
  productToSolr: () => RecentlyViewedProduct;
}): { productTileRef: RefObject<HTMLAnchorElement | null> } => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const productTileRef = useRef<HTMLAnchorElement>(null);
  const trackedRef = useRef<boolean>(false);
  const { pushToDataLayer } = useDataLayerContext();

  const {
    configuration: { defaultCurrency },
    pageType,
  } = useStaticContext();

  const observerCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      setTimeout(() => {
        if (entry?.isIntersecting) {
          const parsedEventProduct = parseProductForImpressions(productToSolr(), position, true);

          const eecImpressionsEvent = getProductDataLayerEvent('eecImpressions', {
            eventProducts: [parsedEventProduct],
            sectionName,
            currency: defaultCurrency,
            pageType,
          });

          if (eecImpressionsEvent && !trackedRef.current && productTileRef.current) {
            pushToDataLayer(eecImpressionsEvent);
            trackedRef.current = true;

            if (productTileRef.current) {
              observerRef.current?.unobserve(productTileRef.current);
            }

            observerRef.current?.disconnect();
            observerRef.current = null;
          }
        }
      });
    },
    [productToSolr, position, sectionName, defaultCurrency, pageType, pushToDataLayer]
  );

  useEffect(() => {
    if (trackedRef.current) {
      return () => {};
    }

    const currentRef = productTileRef.current;

    observerRef.current = new IntersectionObserver(observerCallback, { threshold: 0.85 });

    if (currentRef) {
      observerRef.current?.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observerRef.current?.unobserve(currentRef);
      }

      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [observerCallback, productTileRef]);

  return {
    productTileRef,
  };
};
